/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Inbox, Loader2, FileScan, AlertCircle, CheckCircle2, Trash2,
  RotateCw, ChevronDown, ChevronRight, BookMarked, Zap,
} from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

/**
 * Persistent invoice inbox shown above the additions table.
 *
 * - Lists every pending upload for the current run (Mongo-backed).
 * - Auto-polls every 4 s while ANY row is in 'processing' state.
 * - "Resume" opens the existing preview modal pre-loaded with that upload.
 * - "Discard" deletes the pending row + sidecar chunk PDFs (already-applied
 *   attachments survive on the rows themselves).
 */
function progressLabel(progress) {
  if (!progress?.phase) return "Analysing…";
  if (progress.phase === "classifying") return "Classifying pages…";
  if (progress.phase === "extracting" && progress.batch_total > 1) {
    const current = Math.min((progress.batch_current || 0) + 1, progress.batch_total);
    return `Extracting invoices — batch ${current} of ${progress.batch_total}…`;
  }
  return "Extracting invoices…";
}

export function InvoiceInbox({ rid, refreshKey, onResume, refreshAdditions, activeBlock }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const refresh = async () => {
    if (!rid) return;
    try {
      const { data } = await http.get(`/fixed-assets/runs/${rid}/invoice-inbox`);
      setRows(data?.rows || []);
    } catch (e) {
      if (e?.response?.status && e.response.status !== 404) {
        toast.error(e?.response?.data?.detail || "Could not load invoice inbox");
      }
    } finally {
      setLoaded(true);
    }
  };

  // Initial + reactive load
  useEffect(() => { refresh(); }, [rid, refreshKey]);

  // Auto-poll while anything is processing
  useEffect(() => {
    const anyProcessing = rows.some(r => r.status === "processing");
    if (!anyProcessing) return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [rows]);

  if (!loaded || rows.length === 0) return null;

  // Scope the inbox list to the block the auditor is currently working in.
  // An upload with `blocks: []` (not yet matched against any addition — e.g.
  // still processing, or every chunk needs manual review) has no known
  // block yet, so it stays visible everywhere until it does.
  const visibleRows = activeBlock
    ? rows.filter(r => !r.blocks?.length || r.blocks.includes(activeBlock))
    : rows;

  if (visibleRows.length === 0) return null;

  const total   = visibleRows.length;
  const pending = visibleRows.reduce((s, r) => s + (r.pending || 0), 0);
  const proc    = visibleRows.filter(r => r.status === "processing").length;
  const visibleHighConfPending = visibleRows.reduce((s, r) => s + (r.high_conf_pending || 0), 0);

  const discard = async (uid, filename) => {
    if (!window.confirm(`Discard "${filename}"? Already-attached chunks stay on their rows; only the inbox entry + un-attached chunks are removed.`)) return;
    setBusyId(uid);
    try {
      await http.delete(`/fixed-assets/runs/${rid}/invoice-inbox/${uid}`);
      toast.success("Removed from inbox");
      refresh();
      refreshAdditions?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not discard");
    } finally { setBusyId(null); }
  };

  const sweepAll = async () => {
    if (!visibleHighConfPending) return;
    const uploadCount = visibleRows.filter(r => r.high_conf_pending > 0).length;
    const confirmMsg =
      `Across ${uploadCount} inbox upload${uploadCount === 1 ? "" : "s"}: `
      + `${visibleHighConfPending} high-confidence match${visibleHighConfPending === 1 ? "" : "es"} `
      + `will be attached and ${visibleHighConfPending} asset description${visibleHighConfPending === 1 ? "" : "s"} overwritten.\n\nApply all?`;
    if (!window.confirm(confirmMsg)) return;
    setSweeping(true);
    try {
      const uploadIds = visibleRows.map(r => r.upload_id);
      const { data } = await http.post(`/fixed-assets/runs/${rid}/apply-all-high-confidence`,
        { upload_ids: uploadIds });
      toast.success(
        `${data.total_attached} attached · ${data.total_descriptions} descriptions updated `
        + `across ${data.uploads_processed} upload${data.uploads_processed === 1 ? "" : "s"}`,
      );
      refresh();
      refreshAdditions?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep apply failed");
    } finally { setSweeping(false); }
  };

  return (
    <div className="border border-[#E5E5E0] bg-white" data-testid="fa-invoice-inbox">
      <div className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#FAFAF7]">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-2 text-[12.5px] flex-1 min-w-0 text-left"
          data-testid="fa-invoice-inbox-toggle"
        >
          {collapsed ? <ChevronRight size={13} className="text-slate-500"/> : <ChevronDown size={13} className="text-slate-500"/>}
          <Inbox size={14} className="text-sky-700"/>
          <span className="font-semibold text-slate-800">Invoice Inbox</span>
          <span className="text-slate-500">·</span>
          <span className="font-mono text-[11px] text-slate-600 truncate">
            {total} upload{total === 1 ? "" : "s"}
            {proc > 0 && (
              <span className="ml-2 text-amber-700">
                <Loader2 size={10} className="inline animate-spin mr-0.5"/>
                {proc} processing
              </span>
            )}
            {/* Roll-up badges only add value once there's more than one
                upload to sum across — with a single upload this is the
                exact same number already shown on its row below and just
                reads as a confusing double-count. */}
            {total > 1 && pending > 0 && (
              <span className="ml-2 text-rose-700">
                {pending} chunk{pending === 1 ? "" : "s"} unattached
              </span>
            )}
            {total > 1 && visibleHighConfPending > 0 && (
              <span className="ml-2 text-emerald-800">
                <Zap size={10} className="inline mr-0.5"/>
                {visibleHighConfPending} high-conf
              </span>
            )}
          </span>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          {visibleHighConfPending > 0 && (
            <button
              type="button"
              onClick={sweepAll}
              disabled={sweeping}
              data-testid="fa-invoice-sweep-btn"
              title={`Auto-apply ${visibleHighConfPending} high-confidence matches across the uploads shown here`}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11.5px] bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
            >
              {sweeping ? <Loader2 size={11} className="animate-spin"/> : <Zap size={11}/>}
              Auto-apply {visibleHighConfPending}
            </button>
          )}
          <button
            type="button"
            onClick={refresh}
            className="text-slate-400 hover:text-slate-700 p-1"
            title="Refresh"
          >
            <RotateCw size={11}/>
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="border-t border-[#EDEDE7]">
          <ul className="divide-y divide-[#EDEDE7]">
            {visibleRows.map(r => (
              <li key={r.upload_id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-[#FBFBF8]"
                  data-testid={`fa-inbox-row-${r.upload_id}`}>
                <FileScan size={14} className={
                  r.status === "failed" ? "text-rose-600"
                  : r.status === "processing" ? "text-amber-600"
                  : r.pending === 0 ? "text-emerald-600"
                  : "text-sky-700"
                }/>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium truncate" title={r.filename}>
                    {r.filename}
                  </div>
                  <div className="text-[10.5px] font-mono text-slate-500 mt-0.5 flex items-center flex-wrap gap-2">
                    <span>{(r.pdf_size / 1024).toFixed(0)} KB</span>
                    {r.detected_ledger_name && (
                      <span className="inline-flex items-center gap-0.5 text-sky-700">
                        <BookMarked size={9}/>
                        {r.detected_ledger_name}
                      </span>
                    )}
                    {r.status === "processing" && (
                      <span className="text-amber-700">
                        <Loader2 size={10} className="inline animate-spin mr-0.5"/>
                        {progressLabel(r.progress)}
                      </span>
                    )}
                    {r.status === "failed" && (
                      <span className="text-rose-700 truncate">
                        <AlertCircle size={10} className="inline mr-0.5"/>
                        Failed: {r.error || "Unknown error"}
                      </span>
                    )}
                    {r.status === "done" && (
                      <>
                        <span>{r.total_chunks} chunk{r.total_chunks === 1 ? "" : "s"}</span>
                        {r.applied > 0 && (
                          <span className="text-emerald-700">
                            <CheckCircle2 size={10} className="inline mr-0.5"/>
                            {r.applied} attached
                          </span>
                        )}
                        {r.pending > 0 && (
                          <span className="text-rose-700">{r.pending} pending</span>
                        )}
                        {r.high_conf_pending > 0 && (
                          <span className="text-emerald-800 font-semibold">
                            <Zap size={10} className="inline mr-0.5"/>
                            {r.high_conf_pending} high-conf
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {r.status === "done" && (
                    <button
                      onClick={() => onResume?.(r.upload_id)}
                      data-testid={`fa-inbox-resume-${r.upload_id}`}
                      className="px-2 py-1 text-[11.5px] border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-900"
                    >
                      {r.pending > 0 ? "Resume" : "Review"}
                    </button>
                  )}
                  <button
                    onClick={() => discard(r.upload_id, r.filename)}
                    disabled={busyId === r.upload_id}
                    data-testid={`fa-inbox-discard-${r.upload_id}`}
                    className="text-slate-400 hover:text-rose-700 p-1 disabled:opacity-50"
                    title="Remove from inbox"
                  >
                    {busyId === r.upload_id
                      ? <Loader2 size={11} className="animate-spin"/>
                      : <Trash2 size={11}/>}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
